// LiveSystemCard — live CPU / RAM / network throughput / WAN latency.
// Polls /api/troubleshoot/live-stats every 2s (CPU/RAM/network are smooth at
// that cadence — first response sets the baseline, second onwards has deltas)
// and /api/troubleshoot/network-latency every 10s.

import { useQuery } from '@tanstack/react-query';
import { Activity, Cpu, MemoryStick, Network, Gauge } from 'lucide-react';
import { api } from '../lib/api';

export function LiveSystemCard() {
  const { data: stats } = useQuery<any>({
    queryKey: ['live-stats'],
    queryFn: () => api.liveStats(),
    refetchInterval: 2000,
  });

  const { data: latency } = useQuery<any>({
    queryKey: ['network-latency'],
    queryFn: () => api.networkLatency(),
    refetchInterval: 10000,
  });

  const cpuPct = stats?.cpu?.pct;
  const memPct = stats?.memory?.used_pct;
  const swapPct = stats?.swap?.used_pct;
  const rxBps = stats?.network?.rx_bps;
  const txBps = stats?.network?.tx_bps;
  const iface = stats?.network?.iface || '—';
  const cores = stats?.cpu?.cores;
  const load  = stats?.cpu?.load;

  return (
    <div className="card p-4 space-y-3">
      <h2 className="font-semibold flex items-center gap-2"><Activity size={16}/> Live System</h2>

      <Bar
        icon={<Cpu size={14}/>}
        label="CPU"
        right={cpuPct == null ? '…' : `${cpuPct.toFixed(1)}%`}
        sub={cores ? `${cores} cores · load ${load ? load.map((n: number) => n.toFixed(2)).join(' / ') : '—'}` : ''}
        pct={cpuPct ?? 0}
        color={pctColor(cpuPct)}
      />

      <Bar
        icon={<MemoryStick size={14}/>}
        label="Memory"
        right={memPct == null ? '…' : `${memPct.toFixed(1)}%`}
        sub={stats?.memory ? `${formatBytes(stats.memory.used_bytes)} / ${formatBytes(stats.memory.total_bytes)} used`
                           : '—'}
        pct={memPct ?? 0}
        color={pctColor(memPct)}
      />

      {swapPct != null && (stats?.swap?.total_bytes ?? 0) > 0 && (
        <Bar
          icon={<MemoryStick size={14}/>}
          label="Swap"
          right={`${swapPct.toFixed(1)}%`}
          sub={`${formatBytes(stats.swap.used_bytes)} / ${formatBytes(stats.swap.total_bytes)}`}
          pct={swapPct}
          color={pctColor(swapPct)}
        />
      )}

      <div className="border-t border-border pt-3 space-y-1">
        <div className="flex items-center gap-2 text-sm">
          <Network size={14} className="text-textSecondary"/>
          <span className="font-medium">Network</span>
          <span className="text-xs text-textSecondary font-mono">({iface})</span>
        </div>
        <div className="flex justify-between text-xs font-mono">
          <span><span className="text-textSecondary">↓ down</span> {formatRate(rxBps)}</span>
          <span><span className="text-textSecondary">↑ up</span> {formatRate(txBps)}</span>
        </div>
        <div className="text-xs text-textSecondary">
          Total: ↓ {formatBytes(stats?.network?.rx_total)} / ↑ {formatBytes(stats?.network?.tx_total)}
        </div>
      </div>

      {latency?.targets && (
        <div className="border-t border-border pt-3 space-y-1">
          <div className="flex items-center gap-2 text-sm">
            <Gauge size={14} className="text-textSecondary"/>
            <span className="font-medium">Network latency</span>
          </div>
          {latency.targets.map((t: any) => (
            <div key={t.name} className="flex justify-between text-xs font-mono">
              <span className="text-textSecondary">{t.name}</span>
              <span className={t.ok ? rttColor(t.rtt_ms) : 'text-danger'}>
                {t.ok ? `${t.rtt_ms} ms` : 'unreachable'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Bar({ icon, label, right, sub, pct, color }: {
  icon: any; label: string; right: string; sub: string; pct: number; color: string;
}) {
  return (
    <div className="space-y-1">
      <div className="flex justify-between items-center text-sm">
        <span className="flex items-center gap-2"><span className="text-textSecondary">{icon}</span>{label}</span>
        <span className="font-mono text-xs">{right}</span>
      </div>
      <div className="h-2 bg-elevated rounded-full overflow-hidden">
        <div className={`h-full transition-all duration-700 ${color}`} style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} />
      </div>
      {sub && <div className="text-xs text-textSecondary font-mono">{sub}</div>}
    </div>
  );
}

function pctColor(p: number | null | undefined): string {
  if (p == null) return 'bg-muted';
  if (p < 60) return 'bg-success';
  if (p < 85) return 'bg-warning';
  return 'bg-danger';
}

function rttColor(ms: number | null): string {
  if (ms == null) return 'text-textSecondary';
  if (ms < 50)  return 'text-success';
  if (ms < 150) return 'text-warning';
  return 'text-danger';
}

function formatBytes(b: number | null | undefined): string {
  if (b == null) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1024 ** 2) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 ** 3) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  if (b < 1024 ** 4) return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
  return `${(b / 1024 ** 4).toFixed(2)} TB`;
}

function formatRate(bps: number | null | undefined): string {
  if (bps == null) return '—';
  // bytes/sec → bits/sec for the human-friendly unit
  const bits = bps * 8;
  if (bits < 1000) return `${bits.toFixed(0)} bps`;
  if (bits < 1e6)  return `${(bits / 1000).toFixed(1)} Kbps`;
  if (bits < 1e9)  return `${(bits / 1e6).toFixed(2)} Mbps`;
  return `${(bits / 1e9).toFixed(2)} Gbps`;
}
